import '@assets/styles/tiptap-preview.css';

import { handleApiResponse } from '@src/api/apiClient/axiosClient.ts';
import { companySupportServiceApiClient } from '@src/api/company/supportService/supportServiceApiClient';
import { SupportService } from '@src/api/types/supportService.ts';
import { useCategory } from '@src/context/CategoryContext';
import SupportServiceContent from '@src/features/shared/supportService/SupportServiceContent';
import { useScrollMobile } from '@src/hooks/useScrollMobile';
import { COMPANY_URL, ShowProps } from '@src/routes/company';
import logger from '@src/utils/logger.ts';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { generatePath, useParams } from 'react-router-dom';

function SupportServiceShow({ setFeature, setDescription }: ShowProps) {
  const { id } = useParams<{ id: string }>();
  const [supportService, setSupportService] = useState<SupportService | null>(null);
  const { menuSupportServiceCategories } = useCategory();

  useEffect(() => {
    if (!id) {
      return;
    }

    window.scrollTo(0, 0);

    const fetchData = async () => {
      const res = await companySupportServiceApiClient.show(Number(id));
      handleApiResponse(
        res,
        (data) => {
          setSupportService(data);
          setFeature(data.title);
          setDescription?.(data.description);
        },
        (error) => {
          logger.error('Failed to load articles:', error);
        },
      );
    };

    fetchData();
  }, [id]);

  const { renderScrollMobile } = useScrollMobile({
    items: menuSupportServiceCategories,
  });

  const shareData = supportService
    ? {
        url: `${window.location.origin}${generatePath(COMPANY_URL.SUPPORT_SERVICES_SHOW, { id: supportService.id })}`,
        title: supportService.title,
        tags: supportService.support_service_tags
          ? supportService.support_service_tags.map((tag) => tag.name)
          : [],
        disabled: false,
      }
    : null;

  return (
    <main className='pb-16 pt-10 md:pt-4'>
      <Helmet>
        <meta
          name='description'
          content={supportService?.description}
        />
        <meta
          property='og:title'
          content={supportService?.title}
        />
        <meta
          property='og:description'
          content={supportService?.description}
        />
        {supportService?.thumbnail_url && (
          <meta
            property='og:image'
            content={supportService.thumbnail_url}
          />
        )}
      </Helmet>
      <div className='m-auto max-w-[740px] lg:px-6'>
        <div className='hidden lg:-order-1 lg:block'>
          <div className='ml-[-24px] w-[calc(100%+48px)] pl-[24px]'>{renderScrollMobile()}</div>
        </div>
        {supportService && (
          <SupportServiceContent
            supportService={supportService}
            shareData={shareData}
          />
        )}
      </div>
    </main>
  );
}

export default SupportServiceShow;
